import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TablePagination,
  TableContainer,
  Chip,
} from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import useCustomQuestions from '../../../lib/hooks/useCustomQuestions';
import useMetric from '../../../lib/hooks/useMetric';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { prettyDateTime, toCapitalizeFromUnderScore } from '../../../lib/helpers/helpers';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import PatientCustomQuestionsRow from './PatientCustomQuestionsRow';
import PatientCustomQuestionsSearchFilter from './PatientCustomQuestionsSearchFilter';
import PatientAssessmentLinkComponent from './PatientAssessmentLinkComponent';
import CancelIcon from '@mui/icons-material/Cancel';
import { getStatusChipCss } from '../../../lib/themes/generalTheme';

// Styles only needed by this main component
const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    marginTop: 16,
  },
  topBar: {
    marginBottom: 16,
  },
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
  graphContainer: {
    // Add any existing styles for the graph container
  },
  iconCell: {
    width: '56px',
    padding: '8px 0 8px 16px',
    verticalAlign: 'middle',
  },
  questionCell: {
    width: '170px',
    padding: '0 10px',
  },
  answerCell: {
    width: '333px',
    padding: '0 10px',
  },
  dateCell: {
    width: '117px',
    padding: '0 10px',
  },
  assessmentCell: {
    width: '136px',
    maxWidth: '136px',
    minWidth: '136px',
    padding: '0 10px',
  },
  '@media print': {
    graphContainer: {
      overflow: 'visible',
      transform: 'scale(0.50)',
      transformOrigin: 'top left',
      width: '100%',
      height: 'auto',
      position: 'relative',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  iconButtonAlign: {
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unfoldMoreIcon: {
    fontSize: '24px',
    transition: 'transform 0.2s',
  },
  filterChipsContainer: {
    display: 'flex',
    marginBottom: '16px',
    width: '100%',
    justifyContent: 'flex-end',
  },
}));

// Main component
const PatientCustomQuestionsBox = ({ summary, setSetSubtab, customQuestions, isLoading: externalIsLoading }) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);

  // Track whether we should use props or the hook
  const useProvidedQuestions = customQuestions !== undefined;

  // Call the hook with the current tab ID or undefined based on whether we have provided questions
  // The hook has enabled: !!currentTabId so it won't fetch when currentTabId is undefined
  const { customQuestions: hookCustomQuestions, isLoading: hookIsLoading } = useCustomQuestions(
    useProvidedQuestions ? undefined : currentTab?.id,
  );

  // Determine which data source and loading state to use
  const questionData = useProvidedQuestions ? customQuestions : hookCustomQuestions || [];
  const isLoading = useProvidedQuestions ? externalIsLoading : hookIsLoading;

  // Memoize data and hasData to prevent changes that don't matter
  const data = React.useMemo(() => questionData, [questionData]);
  const hasData = React.useMemo(() => Boolean(data && data.length > 0), [data]);

  const { isLoading: assessmentLoading, data: assessmentData } = useMetric('latest_assessments', currentTab.id);

  // Add state for pagination and expanded rows
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(data || []);

  // Add state for date filtering
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // Filter display state - replace the previous filterData approach
  const [filterChipData, setFilterChipData] = useState(() => ({
    hasActiveFilters: false,
    formattedDate: '',
  }));

  // Check if any filters are applied
  const hasActiveFilters = Boolean(appliedStartDate && appliedEndDate);

  // Memoize filter values to prevent unnecessary rerenders
  const filterValues = React.useMemo(
    () => ({
      searchTerm: hasData ? searchTerm : '',
      appliedStartDate: hasData ? appliedStartDate : '',
      appliedEndDate: hasData ? appliedEndDate : '',
    }),
    [hasData, searchTerm, appliedStartDate, appliedEndDate],
  );

  // Use refs to track previous values and avoid unnecessary updates
  const prevFilterValuesRef = React.useRef({
    searchTerm: '',
    appliedStartDate: '',
    appliedEndDate: '',
  });
  const prevDataRef = React.useRef([]);
  const prevFilteredDataRef = React.useRef([]);

  // Update refs with initial values
  React.useEffect(() => {
    prevFilterValuesRef.current = filterValues;
    prevDataRef.current = data;
    prevFilteredDataRef.current = filteredData;
  }, []); // Empty dependency array ensures this only runs once

  // Initialize filteredData with all data when data changes
  useEffect(() => {
    if (data && Array.isArray(data)) {
      setFilteredData(data);
    }
  }, [data]);

  // Handle search term change
  const handleSearchChange = (event) => {
    // Don't update state if there's no data
    if (!hasData) return;
    setSearchTerm(event.target.value);
  };

  // Handle filter button click
  const handleFilterClick = (event) => {
    // Don't update state if there's no data
    if (!hasData) return;
    setFilterAnchorEl(event.currentTarget);
    // Initialize filter dialog with current values
    setStartDate(appliedStartDate);
    setEndDate(appliedEndDate);
  };

  // Handle filter close
  const handleFilterClose = () => {
    // Don't update state if there's no data
    if (!hasData) return;
    setFilterAnchorEl(null);
  };

  // Handle filter apply
  const handleFilterApply = () => {
    // Don't update state if there's no data
    if (!hasData) return;

    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);

    // Update filter chip data directly here
    const hasActiveFilters = Boolean(startDate && endDate);
    if (hasActiveFilters) {
      // Create formatted date string
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        });
      };

      setFilterChipData({
        hasActiveFilters: true,
        formattedDate: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      });
    }

    handleFilterClose();
  };

  // Handle filter reset
  const handleFilterReset = () => {
    // Don't update state if there's no data
    if (!hasData) return;

    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setFilterChipData({
      hasActiveFilters: false,
      formattedDate: '',
    });
    handleFilterClose();
  };

  // Check if filter is open
  const isFilterOpen = Boolean(filterAnchorEl);
  const filterId = isFilterOpen ? 'filter-popover' : undefined;

  // Filter data based on search term and date filters
  useEffect(() => {
    // Skip if component is not fully initialized
    if (!prevFilterValuesRef.current) return;

    // Skip effect entirely when there's no data
    if (!hasData) {
      if (filteredData.length > 0) {
        setFilteredData([]);
      }
      return;
    }

    try {
      // Check if filter values or data have actually changed
      const filterValuesChanged =
        filterValues.searchTerm !== prevFilterValuesRef.current.searchTerm ||
        filterValues.appliedStartDate !== prevFilterValuesRef.current.appliedStartDate ||
        filterValues.appliedEndDate !== prevFilterValuesRef.current.appliedEndDate;

      const dataChanged = data !== prevDataRef.current;

      // Skip if nothing has changed
      if (!filterValuesChanged && !dataChanged) {
        return;
      }

      // Update refs
      prevFilterValuesRef.current = filterValues;
      prevDataRef.current = data;

      // If no filters are applied, just use all data
      if (!filterValues.searchTerm && !filterValues.appliedStartDate && !filterValues.appliedEndDate) {
        setFilteredData(data);
        return;
      }

      // Use a function to calculate the new filtered data without immediately setting state
      const calculateFilteredData = () => {
        if (!Array.isArray(data)) return [];

        let filtered = [...data];

        // Apply date filters if they exist
        if (filterValues.appliedStartDate && filterValues.appliedEndDate) {
          filtered = filtered.filter((question) => {
            if (!question) return false;
            const questionDate = question?.most_recent_answer?.created_at;
            if (!questionDate) return false;

            try {
              const date = new Date(questionDate);
              if (isNaN(date.getTime())) return false;

              const formattedDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD

              return formattedDate >= filterValues.appliedStartDate && formattedDate <= filterValues.appliedEndDate;
            } catch (e) {
              return false;
            }
          });
        }

        // Apply search term filter
        if (filterValues.searchTerm.trim() !== '') {
          const lowercasedSearchTerm = filterValues.searchTerm.toLowerCase();
          filtered = filtered.filter((question) => {
            if (!question) return false;

            try {
              const questionText = toCapitalizeFromUnderScore(question?.short_name || '').toLowerCase();
              const answerText = (question?.most_recent_answer?.formatted_answer_text || '').toLowerCase();
              const dateText = question?.most_recent_answer?.created_at
                ? prettyDateTime(question.most_recent_answer.created_at).toLowerCase()
                : '';

              return (
                questionText.includes(lowercasedSearchTerm) ||
                answerText.includes(lowercasedSearchTerm) ||
                dateText.includes(lowercasedSearchTerm)
              );
            } catch (e) {
              return false;
            }
          });
        }

        return filtered;
      };

      // Calculate new filtered data
      const newFilteredData = calculateFilteredData();

      // Update filtered data ref
      prevFilteredDataRef.current = newFilteredData;

      // Update state
      setFilteredData(newFilteredData);

      // Reset to first page when filters change
      setPage(0);
    } catch (e) {
      console.error('Error in filter effect:', e);
      // If anything goes wrong, just show all data
      if (data && Array.isArray(data)) {
        setFilteredData(data);
      }
    }
  }, [filterValues, data, hasData, filteredData.length]);

  // Update isLastPage when filtered data changes
  const isLastPage = React.useMemo(() => {
    if (filteredData && filteredData.length) {
      return (page + 1) * rowsPerPage >= filteredData.length;
    }
    return false;
  }, [filteredData, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    if (!hasData) return;
    if (!isLastPage || newPage < page) {
      setPage(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    if (!hasData) return;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle expanded state for a specific row
  const toggleRowExpanded = (id) => {
    if (!hasData) return;
    setExpandedRows({
      ...expandedRows,
      [id]: !expandedRows[id],
    });
  };

  // Toggle expand all rows
  const toggleExpandAll = () => {
    if (!hasData) return;
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);

    // Create a new object with all rows set to the new expand state
    const newExpandedRows = {};
    filteredData?.forEach((question) => {
      newExpandedRows[question.id || question.short_name] = newExpandAll;
    });

    setExpandedRows(newExpandedRows);
  };

  // Function to get assessment link based on workflow ID
  const getAssessmentLink = (workflowId) => {
    if (!workflowId || !assessmentData || !assessmentData.metric || !assessmentData.metric.value) {
      return 'N/A';
    }

    // Find the matching assessment in the assessment data
    const matchingAssessment = assessmentData.metric.value.find((assessment) => assessment.id === workflowId);

    // Return formatted link if data exists, otherwise N/A
    if (matchingAssessment && matchingAssessment.dietQuality && matchingAssessment.dietName) {
      return (
        <PatientAssessmentLinkComponent
          dietQuality={matchingAssessment?.dietQuality}
          dietName={matchingAssessment?.dietName}
          link={matchingAssessment?.workflowShareUrl}
        />
      );
    } else {
      return 'N/A'; // optionally get rid of this because PatientAssessmentLinkComponent will return N/A
    }
  };

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className="patient-custom-questions-box"
          id="patient-custom-questions-box"
        >
          <Grid
            item
            container
            xs={12}
            className={classes.topBar}
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: '100%', maxWidth: '100%', mx: 0, padding: 0 }}
          >
            <div className={classes.titleContainer}>
              <Typography variant={'h2'}>Survey Answers</Typography>

              {!summary && hasData && (
                <PatientCustomQuestionsSearchFilter
                  theme={theme}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterClick={handleFilterClick}
                  hasActiveFilters={hasActiveFilters}
                  filterId={filterId}
                  isFilterOpen={isFilterOpen}
                  filterAnchorEl={filterAnchorEl}
                  handleFilterClose={handleFilterClose}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  handleFilterReset={handleFilterReset}
                  handleFilterApply={handleFilterApply}
                />
              )}
            </div>
          </Grid>

          {/* Render filter chips in a separate row */}
          {!isLoading && hasData && filterChipData.hasActiveFilters && (
            <div className={classes.filterChipsContainer}>
              <Chip
                label={filterChipData.formattedDate}
                onDelete={handleFilterReset}
                deleteIcon={<CancelIcon />}
                style={getStatusChipCss()}
              />
            </div>
          )}

          {isLoading ? (
            <AdminProgressBar />
          ) : data && data.length > 0 ? (
            <Box className={`${classes?.container} ${classes?.graphContainer}`}>
              <TableContainer>
                <Table size="medium">
                  <TableHead className={classes?.tableHead}>
                    <TableRow>
                      <TableCell className={classes.iconCell}>
                        <IconButton
                          className={classes.iconButtonAlign}
                          onClick={toggleExpandAll}
                          size="small"
                        >
                          <UnfoldMore
                            className={classes.unfoldMoreIcon}
                            style={{
                              transform: expandAll ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell className={classes.questionCell}>Question</TableCell>
                      <TableCell className={classes.answerCell}>Answer</TableCell>
                      <TableCell className={classes.dateCell}>Date</TableCell>
                      <TableCell className={classes.assessmentCell}>Assessment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      ?.slice(summary ? 0 : page * rowsPerPage, summary ? 5 : page * rowsPerPage + rowsPerPage)
                      ?.map((question) => (
                        <PatientCustomQuestionsRow
                          key={question?.id || question?.short_name}
                          question={question}
                          toggleRowExpanded={toggleRowExpanded}
                          expandedRows={expandedRows}
                          getAssessmentLink={getAssessmentLink}
                          theme={theme}
                        />
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}

          {!summary && !isLoading && filteredData && filteredData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              getItemAriaLabel={(type) => `Go to ${type} page`}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientCustomQuestionsBox;
